import {
  RepeatBindingsProvider,
  useBespokeSectionContext,
  type RepeatBindings,
} from '../../primitives';
import {
  BESPOKE_EMPTY_STATE_CLASS,
  BESPOKE_EMPTY_STATE_CODE_CLASS,
} from '../../primitives/fieldClassSlots';
import { layoutInlineStyle } from '../layoutInlineStyle';
import type { LayoutNodeChildProps } from '../nodeRenderProps';
import type { RepeatNode } from '../types';
import { RenderNodes } from '../renderNodes';

export function RepeatBlock({
  node,
  answers,
  repeatBindings,
}: LayoutNodeChildProps<RepeatNode>) {
  const { schemaMap } = useBespokeSectionContext();
  if (node.over.kind !== 'multiselectOptionsOf') {
    return null;
  }
  const question = schemaMap.get(node.over.qid);
  const rawValues = question?.values ?? [];
  const options = rawValues.map(val => ({
    value: val,
    label: question?.value_description?.[val]
      ? `${val} - ${question.value_description[val]}`
      : val,
  }));

  if (options.length === 0) {
    return (
      <div className={node.className} style={layoutInlineStyle(node.style)}>
        <p className={BESPOKE_EMPTY_STATE_CLASS}>
          Repeat has nothing to render: multiselect{' '}
          <code className={BESPOKE_EMPTY_STATE_CODE_CLASS}>
            {node.over.qid}
          </code>{' '}
          is missing from the loaded section schema or has no{' '}
          <code className={BESPOKE_EMPTY_STATE_CODE_CLASS}>values</code>. Pick
          the RogerDoc section that contains this question, or replace QIDs with
          ones from that slice.
        </p>
      </div>
    );
  }

  return (
    <div
      className={[`flex flex-col gap-6`, node.className]
        .filter(Boolean)
        .join(' ')}
      style={layoutInlineStyle(node.style)}
    >
      {options.map(opt => {
        const merged: RepeatBindings = { ...repeatBindings, [node.as]: opt };
        return (
          <RepeatBindingsProvider
            key={opt.value}
            parent={repeatBindings}
            merge={{ [node.as]: opt }}
          >
            <RenderNodes
              nodes={node.children}
              answers={answers}
              repeatBindings={merged}
            />
          </RepeatBindingsProvider>
        );
      })}
    </div>
  );
}
